
import { Component, Vue, Watch } from 'vue-property-decorator';
import {
    DeviceInfo,
    // ProximityAlert,
    DeviceType,
} from '@/types/device-info';
import { Action, State, Mutation } from 'vuex-class';
import * as dlg from '@/modules/dlg';
import DeviceQuickActions from '@/components/quick-actions/DeviceQuickActions.vue';
import { ToggleButton } from 'vue-js-toggle-button';
import { UpdateDeviceActionType } from '@/types/store/actions';
import {
    enableDisableOptions,
    onOffOptions,
    scheduleReportOptions,
    alertTimingOptions,
    operatingModeOptions,
    defaultOperatingMode,
    reAlertTimingOptions,
} from '@/constants/options';
import AddNotifyEmailModal from '@/components/devices/popups/AddNotifyEmailModal.vue';
import { mutations, actions } from '@/store/types';
import api from '@/modules/api';
import { SetDeviceMutationType } from '@/types/store/mutations';
import { getErrorMessage } from '@/utils/common';
import { GPSReportModes } from '@/types/device-info';

interface ToneOptions {
    text: string;
    value: number;
    url: string | null;
    audio: HTMLAudioElement | null;
}

@Component({
    components: { DeviceQuickActions, ToggleButton, AddNotifyEmailModal },
})
export default class DeviceAlertsSettings extends Vue {
    @Action(actions.UPDATE_DEVICE)
    updateDevice!: UpdateDeviceActionType;

    @Mutation(mutations.SET_CURRENT_DEVICE)
    setCurrentDevice!: SetDeviceMutationType;

    @State('device')
    device!: DeviceInfo;

    protected toneOptions: ToneOptions[] = [
        {
            text: 'Tone 1',
            value: 0,
            url: '/sounds/tone1.wav',
            audio: null,
        },
        {
            text: 'Tone 2',
            value: 1,
            url: '/sounds/tone2.wav',
            audio: null,
        },
        {
            text: 'Tone 3',
            value: 2,
            url: '/sounds/tone3.wav',
            audio: null,
        },
        {
            text: 'Tone 4',
            value: 3,
            url: null,
            audio: null,
        },
    ];

    liveTrackingIntervalOptions = [
        {
            text: '5 minutes',
            value: 300,
        },
        {
            text: '10 minutes',
            value: 600,
        },
        {
            text: '15 minutes',
            value: 900,
        },
    ];

    protected proximityOptions = [
        {
            text: 'Low sensitivity',
            value: 'low',
        },
        {
            text: 'Medium sensitivity',
            value: 'medium',
        },
        {
            text: 'High sensitivity',
            value: 'high',
        },
    ];

    scheduleReportIntervalOptions = [
        {
            text: '1 minute',
            value: 60,
        },
        {
            text: '2 minute',
            value: 120,
        },
        {
            text: '5 minutes',
            value: 300,
        },
        {
            text: '10 minutes',
            value: 600,
        },
        {
            text: '15 minutes',
            value: 900,
        },
        {
            text: '20 minutes',
            value: 1200,
        },
        {
            text: '25 minutes',
            value: 1500,
        },
        // {
        //     text: '27 minutes',
        //     value: 1620,
        // },
        // {
        //     text: '29 minutes',
        //     value: 1740,
        // },
        {
            text: '30 minutes',
            value: 1800,
        },
        {
            text: '1 hour',
            value: 3600,
        },
        {
            text: '2 hours',
            value: 7200,
        },
        {
            text: '6 hours',
            value: 21600,
        },
        {
            text: '12 hours',
            value: 43200,
        },
        {
            text: 'once a day',
            value: 86400,
        },
    ];

    temperatureUnitOptions = [
        {
            text: 'Celsius (°C)',
            value: 'c',
        },
        {
            text: 'Fahrenheit (°F)',
            value: 'f',
        },
    ];

    alertToneOptions = [
        {
            text: 'Acknowledge',
            value: 1,
        },
        {
            text: 'Success',
            value: 2,
        },
        {
            text: 'Fail',
            value: 3,
        },
        {
            text: 'Single Beep',
            value: 4,
        },
        {
            text: 'Three Beeps',
            value: 5,
        },
        {
            text: 'Alert melody',
            value: 6,
        },
        {
            text: 'Melody',
            value: 7,
        },
        {
            text: 'Melody 2',
            value: 8,
        },
        {
            text: 'Melody 3',
            value: 9,
        },
        {
            text: 'Reset',
            value: 10,
        },
    ];

    temperatureConditionOptions = [
        {
            text: 'Less than or equal',
            value: 'lessOrEqual',
        },
        {
            text: 'Greater than or equal',
            value: 'greaterOrEqual',
        },
    ];

    GPSReportModes = GPSReportModes;
    locationReportMode = GPSReportModes.Once as string;
    operatingMode = defaultOperatingMode;
    addNotifyAlertEmail = false;
    protected enableDisableOptions = enableDisableOptions;
    protected onOffOptions = onOffOptions;
    scheduleReportOptions = scheduleReportOptions;
    alertTimingOptions = alertTimingOptions;
    reAlertTimingOptions = reAlertTimingOptions;
    operatingModeOptions = operatingModeOptions;
    DeviceTypeenum = DeviceType;

    tone = 2;
    // protected proximity: ProximityAlert = ProximityAlert.Low;
    scheduleReport: string[] = [];
    scheduleReportInterval = 60;
    // protected listenToLock = false;
    ledFlash = false;

    temperature = {
        enable: false,
        sendLocation: false,
        reAlertDuration: 5,
        condition: 'lessOrEqual',
        level: 0,
        unit: 'c',
    };

    accelerometer = {
        enable: false,
        ultraPowerMode: false,
        sendLocation: false,
        reAlertDuration: 5,
        sensitivity: 1,
        duration: 5,
    };
    geoFence = {
        enable: false,
        liveTrackingUnable: false,
        trackingTime: 0,
    };

    waterAlarm = {
        enable: false,
        sendLocation: false,
        reAlertDuration: 5,
    };

    pushNotification = false;
    emailAlerts = false;
    locationUpdateNotification = true;
    sosAlertNotification = false;
    notificationEmails: string[] = [];

    mounted() {
        if (![DeviceType.AirboltGps].includes(this.device.deviceType)) {
            Vue.toasted.error('Permission denied');
            return this.$router.push({
                name: 'device-overview',
                params: { deviceUUID: this.device.deviceUUID as string },
            });
        }
    }

    async removePairedAirlock() {
        dlg.notImplemented();
    }

    async save(e: Event) {
        e.preventDefault();
        this.geoFence.trackingTime = this.geoFence.liveTrackingUnable ? this.geoFence.trackingTime : 0;
        await this.updateDevice({
            device: {
                _id: this.device._id,
                tone: this.tone,
                // proximity: this.proximity,
                waterAlarm: this.waterAlarm,
                operatingMode: this.operatingMode,
                scheduleReport: this.scheduleReport,
                scheduleReportInterval: this.scheduleReportInterval,
                // listenToLock: this.listenToLock,
                temperature: this.temperature,
                ledFlash: this.ledFlash,
                geoFence: this.geoFence,
                accelerometer: { ...this.accelerometer, sensitivity: this.reverseSensitivity(this.accelerometer.sensitivity) },
                pushNotification: this.pushNotification,
                emailAlerts: this.emailAlerts,
                locationUpdateNotification: this.locationUpdateNotification,
                sosAlertNotification: this.sosAlertNotification,
            },
        });
    }

    @Watch('device', { immediate: true, deep: true })
    onDeviceChanged(device: DeviceInfo) {
        if (device) {
            this.tone = device.tone;
            // this.proximity = device.proximity || ProximityAlert.Low;
            this.scheduleReport = device.scheduleReport ?? [];
            this.scheduleReportInterval = device.scheduleReportInterval || 1;
            // this.listenToLock = device.listenToLock;
            this.ledFlash = device.ledFlash ?? false;
            this.operatingMode = device.operatingMode ?? defaultOperatingMode;
            this.geoFence = {
                enable: device?.geoFence?.enable ?? false,
                liveTrackingUnable: device?.geoFence?.liveTrackingUnable ?? false,
                trackingTime: device?.geoFence?.liveTrackingUnable ? device?.geoFence?.trackingTime : 0,
            };
            this.temperature = {
                enable: device?.temperature?.enable ?? false,
                sendLocation: device?.temperature?.sendLocation ?? false,
                reAlertDuration: device?.temperature?.reAlertDuration ?? 5,
                condition: device?.temperature?.condition ?? 'lessOrEqual',
                level: device?.temperature?.level ?? 0,
                unit: device?.temperature?.unit ?? 'c',
            };

            this.accelerometer = {
                enable: device?.accelerometer?.enable ?? false,
                ultraPowerMode: device?.accelerometer?.ultraPowerMode ?? false,
                sendLocation: device?.accelerometer?.sendLocation ?? false,
                reAlertDuration: device?.accelerometer?.reAlertDuration ?? 5,
                sensitivity: this.reverseSensitivity(device?.accelerometer?.sensitivity ?? 1),
                duration: device?.accelerometer?.duration ?? 5,
            };

            this.waterAlarm = {
                enable: device?.waterAlarm?.enable ?? false,
                sendLocation: device?.waterAlarm?.sendLocation ?? false,
                reAlertDuration: device?.waterAlarm?.reAlertDuration ?? 5,
            };

            this.pushNotification = device.pushNotification ?? false;
            this.emailAlerts = device.emailAlerts ?? false;
            this.locationUpdateNotification = device.locationUpdateNotification;
            this.sosAlertNotification = device.sosAlertNotification;
            this.notificationEmails = device.notificationEmails ?? [];
            this.locationReportMode = device.locationReportMode;
        }
    }

    handlePlay() {
        const toneOption = this.toneOptions[this.tone];

        if (!toneOption?.url) {
            return;
        }

        if (!toneOption.audio) {
            toneOption.audio = new Audio(toneOption.url);
        }

        toneOption.audio.play();
    }

    async removeNotificationEmail(email: string) {
        const confirmed = await dlg.confirm('Are you sure you want to remove this email?', 'Email removal');
        if (!confirmed) return;

        this.$store.commit(mutations.LOADING, { loading: true });
        try {
            const response = await api.removeNotifyEmail(this.device.deviceUUID, email);
            const updated = {
                ...this.device,
                notificationEmails: response.data.notificationEmails,
            };
            this.setCurrentDevice({ device: updated });
            Vue.toasted.success(`Removed successfully`);
        } catch (error) {
            Vue.toasted.error(getErrorMessage(error));
        } finally {
            this.$store.commit(mutations.LOADING, { loading: false });
        }
    }

    get toneAudio() {
        const toneOption = this.toneOptions[this.tone];
        return toneOption?.url;
    }

    onScheduleReportChanged(value: string[]) {
        let update = value;
        if (this.scheduleReport.includes('gps') && !value.includes('gps') && value.includes('cell'))
            update = value.filter((e) => e !== 'cell');
        else if (value.includes('cell') && !value.includes('gps')) update = [...value, 'gps'];
        this.scheduleReport = update;
    }

    reverseSensitivity(sensitivity: number) { return 11 - sensitivity}
}
